import type { Environment, PartnerClient, Scope } from '@epilot/sdk/identity'
import { z } from 'zod'

// The SDK ships these unions as types only; the runtime lists are pinned to them here so a value
// added to the API without a UI update fails typecheck instead of drifting silently.
export const SCOPES = [
  'openid',
  'email',
  'profile'
] as const satisfies readonly Scope[]
export const ENVIRONMENTS = [
  'dev',
  'staging',
  'production'
] as const satisfies readonly Environment[]

const httpsUrl = z.string().refine((value) => {
  try {
    const url = new URL(value)

    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !value.includes('#')
    )
  } catch {
    return false
  }
}, 'Use an absolute HTTPS URL without credentials or a fragment.')

// Containing openid also makes the list non-empty, as the API requires.
const scopes = z
  .array(z.enum(SCOPES))
  .refine(
    (value): value is [Scope, ...Scope[]] => value.includes('openid'),
    'The openid scope is required.'
  )

export const lines = (value: string) =>
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
export const clientSchema = z
  .object({
    partner_key: z
      .string()
      .min(1)
      .max(64)
      .regex(
        /^[a-z0-9][a-z0-9-]*$/,
        'Use lowercase letters, numbers and hyphens for the partner key.'
      ),
    display_name: z.string().trim().min(1).max(128),
    environment: z.enum(ENVIRONMENTS),
    description: z.string().max(1024).optional(),
    redirect_uris: z
      .array(httpsUrl)
      .nonempty('Add at least one redirect URI.')
      .refine(
        (uris) => new Set(uris).size === uris.length,
        'Redirect URIs must be unique.'
      ),
    scopes_allowed: scopes,
    sector_identifier_uri: httpsUrl.optional(),
    initiate_login_uri: httpsUrl.optional(),
    launch_target_prefixes: z
      .array(
        httpsUrl.refine((value) => {
          try {
            return !new URL(value).search
          } catch {
            return false
          }
        }, 'Launch prefixes must be valid URLs without a query string.')
      )
      .optional()
  })
  .superRefine((value, ctx) => {
    // Field errors already describe malformed URLs.
    const urls = value.redirect_uris.map((uri) => {
      try {
        return new URL(uri)
      } catch {
        return null
      }
    })

    if (
      urls.every(Boolean) &&
      new Set(urls.map((url) => url!.host)).size > 1 &&
      !value.sector_identifier_uri
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['sector_identifier_uri'],
        message: 'Redirects on different hosts require a sector identifier URL.'
      })
    }
    if (value.launch_target_prefixes?.length && !value.initiate_login_uri) {
      ctx.addIssue({
        code: 'custom',
        path: ['initiate_login_uri'],
        message: 'Launch prefixes require a login initiation URL.'
      })
    }
  })

export const organizationSchema = z.object({
  org_id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/),
  authorization_policy: z.object({
    allowed_scopes: scopes,
    customer_approval_ref: z
      .string()
      .trim()
      .min(1, 'Record the customer’s approval reference.')
      .max(512)
  })
})

export const sectorHost = (
  client: Pick<PartnerClient, 'redirect_uris' | 'sector_identifier_uri'>
) => {
  try {
    return new URL(client.sector_identifier_uri || client.redirect_uris[0]).host
  } catch {
    return undefined
  }
}
